import React from 'react';
import { TERMS_TEXT } from '../lib/terms-content';
import { GERMAN_TERMS_TEXT } from '../lib/terms-content-de';
import { useT, brandize } from '../i18n';
import { isDragonTiger, isGerman, isBrazil } from '../lib/app-variant';

// CF-GERMAN-LEGAL-TRANSLATE-V1 (PL #9155): the german (Münzo) build renders a
// fully-German Terms from a dedicated PURE content module (terms-content-de.ts),
// NOT the English TERMS_TEXT patched at render time. This supersedes the rebrand's
// germanizeContacts() helper (now obsolete: GERMAN_TERMS_TEXT already carries the
// Münzo brand, the support@muenzo.xyz contact, and 0 English). default + dragontiger
// keep the English TERMS_TEXT via brandize() — and the rendered output for those two
// is byte-identical to before, because the removed germanizeContacts() was an identity
// transform for any non-german build (it early-returned when isGerman === false).

/**
 * CF-MAINNET-71 + CF-MAINNET-75: /terms page.
 *
 * CF-75 (2026-04-24T09:21Z Scorer scope-sign): PL flagged raw `##` markdown
 * rendering literally because the earlier <pre> approach preserved whitespace
 * without parsing. Swapped for a minimal inline markdown-to-JSX parser
 * supporting only the syntax we actually use (#/##/###, - lists, blank lines
 * as paragraph breaks). No markdown lib dependency (bounded syntax, one doc).
 *
 * Scope discipline: no HTML passthrough, no general-markdown support, no
 * inline code/emphasis. If the TOS ever needs bold/italic/tables we revisit.
 */

function renderTerms(text: string): React.ReactNode[] {
    const lines = text.split('\n');
    const out: React.ReactNode[] = [];
    let para: string[] = [];
    let list: string[] = [];
    let key = 0;
    const flushPara = () => {
        if (para.length) {
            out.push(<p key={key++} style={{ margin: '0 0 1em 0', lineHeight: 1.65 }}>{para.join(' ')}</p>);
            para = [];
        }
    };
    const flushList = () => {
        if (list.length) {
            out.push(
                <ul key={key++} style={{ margin: '0 0 1em 1.5em', padding: 0, lineHeight: 1.65 }}>
                    {list.map((l, i) => <li key={i} style={{ marginBottom: '0.35em' }}>{l}</li>)}
                </ul>,
            );
            list = [];
        }
    };
    for (const rawLine of lines) {
        const line = rawLine.trimEnd();
        if (line.startsWith('### ')) {
            flushPara(); flushList();
            out.push(<h3 key={key++} style={{ color: 'var(--gold)', marginTop: '1.2em', marginBottom: '0.5em', fontSize: '0.95em' }}>{line.slice(4)}</h3>);
        } else if (line.startsWith('## ')) {
            flushPara(); flushList();
            out.push(<h2 key={key++} style={{ color: 'var(--gold)', marginTop: '1.6em', marginBottom: '0.6em', fontSize: '1.1em' }}>{line.slice(3)}</h2>);
        } else if (line.startsWith('# ')) {
            flushPara(); flushList();
            out.push(<h1 key={key++} style={{ color: '#fff', marginTop: '0.2em', marginBottom: '0.8em', fontSize: '1.4em' }}>{line.slice(2)}</h1>);
        } else if (line.startsWith('- ')) {
            flushPara();
            list.push(line.slice(2));
        } else if (line === '') {
            flushPara(); flushList();
        } else {
            flushList();
            para.push(line);
        }
    }
    flushPara(); flushList();
    return out;
}

export function TermsPage() {
    const t = useT();
    // German build → fully-German Terms (already brand-correct, 0 English); default +
    // dragontiger → English TERMS_TEXT folded to the variant brand by brandize().
    // TODO(P2/PL): MoedaFlip PT legal copy pending lawyer verbatim — the brazil build
    // intentionally falls back to the brandized English TERMS_TEXT (real legal) until the
    // PT Terms are authored, mirroring how dragontiger renders the English body.
    const body = isGerman ? GERMAN_TERMS_TEXT : brandize(TERMS_TEXT);
    return (
        <div
            style={{
                minHeight: '100vh',
                background: '#070b14',
                color: '#cbd2dd',
                padding: '24px 16px',
            }}
        >
            <div style={{ maxWidth: 720, margin: '0 auto', fontSize: 14 }}>
                <a
                    href="/"
                    style={{
                        display: 'inline-block',
                        color: 'var(--gold)',
                        textDecoration: 'none',
                        marginBottom: 16,
                        fontSize: 14,
                    }}
                >
                    {t('common.back_to_cricketflip')}
                </a>
                {/* dragontiger (zh) still renders the English legal body, so it keeps the
                    "English is the controlling version" notice. The german (Münzo) build
                    now renders a fully-German body (CF-GERMAN-LEGAL-TRANSLATE-V1), so the
                    notice no longer applies and is dropped for german. The brazil (MoedaFlip)
                    build renders the brandized English body (PT legal pending — see TODO
                    above), so it keeps the English-controlling notice too. */}
                {(isDragonTiger || isBrazil) && (
                    <div
                        style={{
                            border: '1px solid var(--gold)',
                            borderRadius: 8,
                            padding: '10px 12px',
                            margin: '0 0 18px 0',
                            fontSize: 13,
                            lineHeight: 1.6,
                            color: '#cbd2dd',
                            background: 'rgba(212,175,55,0.06)',
                        }}
                    >
                        {t('legal.english_controlling_notice')}
                    </div>
                )}
                {renderTerms(body)}
            </div>
        </div>
    );
}
